import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

import { matchDestinations, searchIndex, type Destination } from '../search';
import { useSearchSources, type SearchOption } from './useSearchSources';

/** A single letter would list half the portal. */
const MIN_LENGTH = 2;

function toOption(destination: Destination): SearchOption {
  return {
    key: `${destination.group}:${destination.path}:${destination.label}`,
    label: destination.label,
    group: destination.group,
    // Under a sub-page, the section it lives in; under a section, what it does.
    secondary: destination.section ?? destination.hint,
    path: destination.path,
  };
}

/**
 * The top bar's search: pages by name, and for staff, organizations too.
 *
 * MUI's Autocomplete is the combobox pattern - Down opens and moves through
 * the results with the active one announced, Enter chooses, Escape closes -
 * so nothing here handles a key itself. The list only ever holds matches, so
 * the component's own filtering is switched off.
 */
export function GlobalSearch() {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const searchId = useId();

  const index = useMemo(
    () => searchIndex(user?.role ?? null, Boolean(viewing)),
    [user?.role, viewing],
  );
  const sources = useSearchSources(inputValue);
  const enough = inputValue.trim().length >= MIN_LENGTH;
  const options = enough
    ? [...matchDestinations(index, inputValue).map(toOption), ...sources]
    : [];

  return (
    <Autocomplete<SearchOption>
      options={options}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        setInputValue(value);
        if (reason === 'input') setOpen(true);
      }}
      onChange={(_, option) => {
        if (!option?.path) return;
        setInputValue('');
        setOpen(false);
        void navigate(option.path);
      }}
      open={open && enough}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterOptions={(shown) => shown}
      groupBy={(option) => option.group}
      // Each group a labelled group of options. MUI's own markup put the
      // options in a plain list inside a list item, which a screen reader
      // cannot follow as one listbox - the accessibility scan failed it.
      renderGroup={(params) => {
        const labelId = `${searchId}-group-${String(params.key)}`;
        return (
          <Box
            component="li"
            key={params.key}
            role="group"
            aria-labelledby={labelId}
            sx={{ listStyle: 'none' }}
          >
            <ListSubheader id={labelId} component="div" role="presentation">
              {params.group}
            </ListSubheader>
            <Box component="ul" role="presentation" sx={{ p: 0, m: 0 }}>
              {params.children}
            </Box>
          </Box>
        );
      }}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.key}
      getOptionDisabled={(option) => Boolean(option.disabled)}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      noOptionsText="No pages match"
      // The first match is the active one, so a few letters and Enter is enough.
      autoHighlight
      openOnFocus={false}
      clearOnBlur
      popupIcon={null}
      forcePopupIcon={false}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={key} {...rest}>
            <Box sx={{ minWidth: 0 }}>
              <Typography>{option.label}</Typography>
              {option.secondary ? (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {option.secondary}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search"
          size="small"
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, 'aria-label': 'Search' },
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      slotProps={{
        popper: {
          // Opens leftwards from the box's right edge, so on a phone - where
          // the box is narrow and near the right - the list stays on screen.
          placement: 'bottom-end',
          sx: { width: 'min(360px, calc(100vw - 32px)) !important' },
        },
      }}
      sx={{ width: { xs: 140, sm: 240 } }}
    />
  );
}
